import {
  Coin,
  Icon,
  InlineStack,
  Link,
  Panel,
  PanelActions,
  PanelHeader,
  PanelSection,
  PanelTitle,
  PlainLink,
  Text,
  Tooltip,
} from '@customerio/pluma-components/react';
import type { ComponentProps } from 'react';
import type { IntegrationConnection } from '../../connections/view-model/types';
import * as styles from './api-keys-security-card.css';

type IconName = ComponentProps<typeof Icon>['name'];

type DisplayItem = {
  key: string;
  icon: IconName;
  text: string;
  href: string;
  isDefault: boolean;
  isExternal: boolean;
  tooltip?: string;
  linkWithSafeExternalAttributes: boolean;
};

/** Pluma icon per mobile-SDK connection `type` (the backend's native slug). */
const MOBILE_ICONS: Record<string, IconName> = {
  ios: 'apple',
  android: 'android',
  reactnative: 'react-native',
};

const DEFAULT_ITEMS: DisplayItem[] = [
  {
    key: 'customerio-track',
    icon: 'cio-logo',
    text: 'Customer.io Track',
    tooltip: 'These credentials are required to authenticate with the AP',
    href: '/settings/api_credentials?keyType=tracking',
    isDefault: true,
    isExternal: false,
    linkWithSafeExternalAttributes: false,
  },
  {
    key: 'customerio-app',
    icon: 'cio-logo',
    text: 'Customer.io App',
    tooltip: 'These keys allow you to send data to the App API. They do not provide access to the Track API.',
    href: '/settings/api_credentials?keyType=tracking',
    isDefault: true,
    isExternal: false,
    linkWithSafeExternalAttributes: false,
  },
  {
    key: 'webhook-signing-key',
    icon: 'webhook',
    text: 'Webhook signing key',
    tooltip: 'Reporting Webhooks send real-time message activity events',
    href: 'https://docs.customer.io/journeys/webhooks/',
    isDefault: true,
    isExternal: true,
    linkWithSafeExternalAttributes: true,
  },
];

type ApiKeysSecurityCardProps = {
  /** The mobile-SDK connections to append as rows (each links to its Pipelines settings page). */
  connections: IntegrationConnection[];
};

/**
 * The "API keys & security" panel on the Developers sub-tab: three fixed rows
 * (Track / App credentials and the webhook signing key) plus one row per
 * connected mobile SDK. The mobile rows point into the Pipelines app, so they
 * are full-page-load anchors, not router links.
 */
export function ApiKeysSecurityCard({ connections }: ApiKeysSecurityCardProps) {
  const items: DisplayItem[] = [
    ...DEFAULT_ITEMS,
    ...connections.map(
      (connection): DisplayItem => ({
        key: connection.id,
        icon: MOBILE_ICONS[connection.type],
        text: connection.name,
        href: `${connection.path}/settings`,
        isDefault: false,
        isExternal: true,
        linkWithSafeExternalAttributes: false,
      }),
    ),
  ];

  return (
    <Panel withSectionBorders>
      <PanelHeader>
        <PanelTitle level="3" data-test-api-keys-heading>
          API keys &amp; security
        </PanelTitle>
        <PanelActions>
          <Link data-test-docs-link iconPosition="trailing" href="https://customer.io" isExternal icon="exit">
            View docs
          </Link>
        </PanelActions>
      </PanelHeader>

      {items.map((item) => (
        <PanelSection key={item.key} className={styles.item}>
          <PlainLink
            data-test-api-key-item={item.text}
            href={item.href}
            isExternal={item.isExternal}
            withSafeExternalAttributes={item.linkWithSafeExternalAttributes}
          >
            <InlineStack alignInline="space-between">
              <InlineStack gap="200">
                <Coin size="sm">
                  <Icon name={item.icon} />
                </Coin>
                <InlineStack gap="050" alignBlock="center">
                  <Text>{item.text}</Text>
                  {item.tooltip != null ? (
                    <Tooltip content={item.tooltip}>
                      <Icon size="sm" name="info" color="subtle" />
                    </Tooltip>
                  ) : null}
                </InlineStack>
              </InlineStack>
              <Icon name={item.isDefault ? 'exit' : 'chevron-right'} size="sm" />
            </InlineStack>
          </PlainLink>
        </PanelSection>
      ))}
    </Panel>
  );
}
